import {
  DirectoryClient,
  buildDirectoryQueryParams,
  createDirectoryDetailFetchers,
  mapDirectoryRecordsToSearchResultItems,
  mapDirectoryRecordToSearchResultItem,
} from '@centivo/provider-directory-components'
import type {
  DirectoryDetailFetchers,
  DirectoryPractitionerRecord,
  DirectorySharedParams,
  FetchInstitutionProviders,
  FetchProviderLocations,
  FetchSearchResults,
  FetchProviderDetail,
  FetchInstitutionDetail,
  ProviderSummary,
  SearchParams,
} from '@centivo/provider-directory-components'

/** Matches the library's detail-fetch page size cap for on-demand rosters. */
const INSTITUTION_ROSTER_PAGE_SIZE = 100

export interface DirectorySearchAdapterOptions {
  baseUrl: string
  networks: string[]
  preferredNetworkCodes?: string[]
  getAuthHeaders?: () =>
    | Promise<Record<string, string>>
    | Record<string, string>
  searchParams?: SearchParams | null
}

export interface DirectorySearchAdapter {
  fetchSearchResults: FetchSearchResults
  /**
   * Loads a practitioner's additional locations on demand (the "other
   * locations" disclosure) via geo search (`/directory/search`), matching
   * the ProviderDirectory story wiring.
   */
  fetchProviderLocations: FetchProviderLocations
  /**
   * Loads an institution's nested providers on demand (the providers
   * disclosure), constrained by the same filters/search scope as the results.
   */
  fetchInstitutionProviders: FetchInstitutionProviders
  fetchProviderDetail: FetchProviderDetail
  fetchInstitutionDetail: FetchInstitutionDetail
  fetchProviderLocationsForProfile: FetchProviderLocations
  searchProviderLocations: DirectoryDetailFetchers['searchProviderLocations']
}

/**
 * Wraps the library `DirectoryClient` in a `fetchSearchResults` adapter for
 * `useSearchResultsPage`, mirroring the ProviderDirectory story's real fetcher
 * but sourcing config (base URL, networks, preferred networks) from oe-portal.
 */
export function createDirectorySearchAdapter(
  options: DirectorySearchAdapterOptions
): DirectorySearchAdapter {
  const client = new DirectoryClient({
    baseUrl: options.baseUrl,
    getAuthHeaders: options.getAuthHeaders,
  })

  // The site's configured preferred networks are the authoritative input. They
  // are plan series codes (e.g. "905") that the directory backend crosswalks to
  // provider-domain network IDs on every request. We must NOT feed back the
  // server's echoed `meta.preferredNetworks` — those are the *resolved* network
  // IDs (e.g. "amed01"), which the backend can't re-crosswalk, so preferred
  // sorting would silently break on the second request onward.
  const preferredNetworks = options.preferredNetworkCodes ?? []

  const fetchSearchResults: FetchSearchResults = async (params, context) => {
    if (options.networks.length === 0) {
      return { items: [], total: 0, specialtyFilters: [], languageFilters: [] }
    }

    const { searchParams: directoryParams } = buildDirectoryQueryParams(
      params,
      context.filters,
      {
        networks: options.networks,
        careType: context.careType,
        sortBy: context.sortBy,
        preferredNetworkCodes: preferredNetworks,
      }
    )

    if (!directoryParams) {
      return { items: [], total: 0, specialtyFilters: [], languageFilters: [] }
    }

    const response = await client.searchPage(directoryParams, {
      page: context.page,
      pageSize: context.pageSize,
      signal: context.signal,
    })

    return {
      items: mapDirectoryRecordsToSearchResultItems(response.data),
      total: response.count.total,
      specialtyFilters: response.displayFilters.specialty,
      languageFilters: Object.keys(response.displayFilters.language),
      hasPreferredProviders: (response.count.isPreferred ?? 0) > 0,
    }
  }

  const profileFetchers = createDirectoryDetailFetchers(client, {
    networks: options.networks,
    preferredNetworkCodes:
      preferredNetworks.length > 0 ? preferredNetworks : undefined,
  })

  const loadProviderLocationsViaSearch: FetchProviderLocations = (
    provider,
    context
  ) => {
    const placeId = options.searchParams?.placeId
    if (!placeId) return Promise.resolve([])
    return profileFetchers.searchProviderLocations(provider, {
      signal: context.signal,
      placeId,
      distance: context.distance,
      fetchParams: context.fetchParams,
    })
  }

  const fetchProviderLocations = loadProviderLocationsViaSearch

  const fetchLocationRoster = async (
    locationId: string,
    signal: AbortSignal | undefined,
    fetchParams?: DirectorySharedParams
  ): Promise<DirectoryPractitionerRecord[]> => {
    const response = await client.fetch(
      {
        ...(fetchParams ?? {}),
        networks: options.networks,
        ...(preferredNetworks.length > 0
          ? { preferredNetworkCodes: preferredNetworks }
          : {}),
        pageSize: INSTITUTION_ROSTER_PAGE_SIZE,
        locationId,
        collapseMode: 'none',
        entityTypes: ['practitioner'],
      },
      { signal }
    )
    return response.data.filter(
      (record): record is DirectoryPractitionerRecord =>
        record.entityType === 'practitioner'
    )
  }

  const fetchInstitutionProviders: FetchInstitutionProviders = async (
    institution,
    context
  ) => {
    const locationId = institution.location?.id
    if (!locationId) return []

    const records = await fetchLocationRoster(
      locationId,
      context.signal,
      context.fetchParams
    )
    const providers: ProviderSummary[] = []
    records.forEach(record => {
      const item = mapDirectoryRecordToSearchResultItem(record)
      if (item.kind === 'practitioner') providers.push(item.data)
    })
    return providers
  }

  const fetchProviderDetail: FetchProviderDetail = (id, context) =>
    profileFetchers.fetchProviderDetail(id, context)

  const fetchInstitutionDetail: FetchInstitutionDetail = (id, context) =>
    profileFetchers.fetchInstitutionDetail(id, context)

  const fetchProviderLocationsForProfile = loadProviderLocationsViaSearch

  return {
    fetchSearchResults,
    fetchProviderLocations,
    fetchInstitutionProviders,
    fetchProviderDetail,
    fetchInstitutionDetail,
    fetchProviderLocationsForProfile,
    searchProviderLocations: profileFetchers.searchProviderLocations,
  }
}
